import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Search, Calendar, RefreshCw,
  Filter, X, ArrowUpCircle, ArrowDownCircle, AlertTriangle
} from 'lucide-react';
import { adminService } from '../../services';
import { LoadingPage } from '../../components/ui/Loading';
import { formatDate } from '../../utils/dateUtils';

const STATUS_LABELS = {
  ok: 'OK',
  partial: 'Parcial',
  no_alistado: 'Sem alistado',
  no_elegivel: 'Sem elegível',
  no_removivel: 'Sem vaga',
  login_failed: 'Falha no login',
  parse_error: 'Erro de parse',
  error: 'Erro',
};

const STATUS_COLORS = {
  ok: 'text-green-400 bg-green-500/10 border-green-500/20',
  partial: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  no_alistado: 'text-gray-400 bg-gray-500/10 border-gray-500/20',
  no_elegivel: 'text-gray-400 bg-gray-500/10 border-gray-500/20',
  no_removivel: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  login_failed: 'text-red-400 bg-red-500/10 border-red-500/20',
  parse_error: 'text-red-400 bg-red-500/10 border-red-500/20',
  error: 'text-red-400 bg-red-500/10 border-red-500/20',
};

const AdminClanRotationLogReport = () => {
  const navigate = useNavigate();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dryRunFilter, setDryRunFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 20;

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = { page: currentPage, limit: itemsPerPage };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (statusFilter) params.status = statusFilter;
      if (dryRunFilter) params.dryRun = dryRunFilter;
      if (sourceFilter) params.triggerSource = sourceFilter;
      if (startDate) params.startDate = `${startDate} 00:00:00`;
      if (endDate) params.endDate = `${endDate} 23:59:59`;

      const data = await adminService.getClanRotationLog(params);
      setLogs(data.logs || []);
      setTotalPages(data.pagination?.pages || 1);
      setTotalItems(data.pagination?.total || 0);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Você não tem permissão para acessar esta página.');
      } else {
        setError(err.response?.data?.error || 'Erro ao carregar log de rotatividade do clã');
      }
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, statusFilter, dryRunFilter, sourceFilter, startDate, endDate]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }
  };

  const handleFilterChange = (setter) => (value) => {
    setter(value);
    setCurrentPage(1);
  };

  const handleDateChange = (setter) => (e) => {
    setter(e.target.value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setStatusFilter('');
    setDryRunFilter('');
    setSourceFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const hasActiveFilters = searchTerm || statusFilter || dryRunFilter || sourceFilter || startDate || endDate;

  if (loading && logs.length === 0) {
    return <LoadingPage message="Carregando log..." title="Log de Rotatividade do Clã" />;
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6 overflow-hidden">
      <div className="max-w-full mx-auto flex flex-col gap-4 h-full min-h-0 w-full">

        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-4 sm:p-5 border border-white/10 shadow-xl">
          <div className="flex items-center gap-4 mb-4">
            <button onClick={() => navigate('/app/admin')}
              className="w-10 h-10 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-gray-300 hover:text-white transition-all flex items-center justify-center">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent flex items-center gap-3">
                <RefreshCw className="w-7 h-7 text-cyan-400" />
                Log de Rotatividade do Clã
              </h1>
              <p className="text-gray-400 mt-1 text-sm">
                Execuções da automação que aceita Alistados e remove Membros ausentes no clã
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                <Search className="w-3 h-3" /> Buscar
              </label>
              <input type="text" value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                placeholder="Nome, detalhe ou erro... (Enter para buscar)"
                className="w-full px-3 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                <Filter className="w-3 h-3" /> Status
              </label>
              <select value={statusFilter} onChange={e => handleFilterChange(setStatusFilter)(e.target.value)}
                className="px-3 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50">
                <option value="">Todos</option>
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                <Filter className="w-3 h-3" /> Modo
              </label>
              <select value={dryRunFilter} onChange={e => handleFilterChange(setDryRunFilter)(e.target.value)}
                className="px-3 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50">
                <option value="">Todos</option>
                <option value="false">Real</option>
                <option value="true">Simulação</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                <Filter className="w-3 h-3" /> Origem
              </label>
              <select value={sourceFilter} onChange={e => handleFilterChange(setSourceFilter)(e.target.value)}
                className="px-3 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50">
                <option value="">Todas</option>
                <option value="http">HTTP</option>
                <option value="cli">CLI</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                <Calendar className="w-3 h-3" /> De
              </label>
              <input type="date" value={startDate} onChange={handleDateChange(setStartDate)}
                className="px-3 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Até
              </label>
              <input type="date" value={endDate} onChange={handleDateChange(setEndDate)}
                className="px-3 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
            </div>
            {hasActiveFilters && (
              <button onClick={clearFilters}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10 transition-all">
                <X className="w-4 h-4" /> Limpar
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="flex-shrink-0 bg-red-900/20 rounded-xl p-4 border border-red-500/20 text-red-400 text-center">{error}</div>
        )}

        {/* Table */}
        <div className="flex-1 min-h-0 bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl overflow-hidden flex flex-col">
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm" style={{ minWidth: '1100px' }}>
              <thead className="sticky top-0 z-20">
                <tr className="border-b border-white/10 bg-slate-800/95">
                  <th className="text-left px-4 py-3 text-gray-400 font-medium sticky left-0 bg-slate-800/95 z-30 whitespace-nowrap">Data</th>
                  <th className="text-center px-3 py-3 text-gray-400 font-medium whitespace-nowrap">Status</th>
                  <th className="text-center px-3 py-3 text-gray-400 font-medium whitespace-nowrap">Modo</th>
                  <th className="text-center px-3 py-3 text-gray-400 font-medium whitespace-nowrap">Origem</th>
                  <th className="text-center px-3 py-3 text-gray-400 font-medium whitespace-nowrap">Lidos</th>
                  <th className="text-left px-3 py-3 text-gray-400 font-medium">Aceitos</th>
                  <th className="text-left px-3 py-3 text-gray-400 font-medium">Removidos</th>
                  <th className="text-left px-3 py-3 text-gray-400 font-medium">Detalhe</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-16 text-gray-400">
                      <RefreshCw className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Nenhum registro encontrado</p>
                    </td>
                  </tr>
                ) : logs.map((log) => (
                  <tr key={log.id} className="border-b border-white/5 hover:bg-white/5 transition-colors align-top">
                    <td className="px-4 py-3 sticky left-0 bg-slate-800/95 z-10 whitespace-nowrap text-gray-300 text-xs">
                      {log.started_at ? formatDate(log.started_at) : (log.created_at ? formatDate(log.created_at) : '—')}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border whitespace-nowrap ${
                        STATUS_COLORS[log.status] || 'text-gray-400 bg-gray-500/10 border-gray-500/20'
                      }`}>
                        {['login_failed', 'parse_error', 'error'].includes(log.status) && <AlertTriangle className="w-3 h-3" />}
                        {STATUS_LABELS[log.status] || log.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 rounded-lg text-xs font-medium border ${
                        log.dry_run
                          ? 'text-amber-400 bg-amber-500/10 border-amber-500/20'
                          : 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20'
                      }`}>
                        {log.dry_run ? 'Simulação' : 'Real'}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center text-gray-300 text-xs whitespace-nowrap uppercase">
                      {log.trigger_source || '—'}
                    </td>
                    <td className="px-3 py-3 text-center text-gray-300 whitespace-nowrap">
                      {log.members_read ?? '—'}
                    </td>
                    <td className="px-3 py-3 max-w-[220px]">
                      {(log.accepted || []).length === 0 ? (
                        <span className="text-gray-600 text-xs">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {log.accepted.map((name, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-500/10 border border-green-500/20 rounded text-xs text-green-400 whitespace-nowrap">
                              <ArrowUpCircle className="w-3 h-3" /> {name}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 max-w-[220px]">
                      {(log.removed || []).length === 0 ? (
                        <span className="text-gray-600 text-xs">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {log.removed.map((name, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400 whitespace-nowrap">
                              <ArrowDownCircle className="w-3 h-3" /> {name}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-gray-400 text-xs max-w-[320px]">
                      {log.error ? (
                        <span title={log.error} className="text-red-400 line-clamp-2">{log.error}</span>
                      ) : (
                        <span title={log.detail} className="line-clamp-2">{log.detail || '—'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex-shrink-0 px-4 py-3 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-gray-500">
              {totalItems > 0
                ? `${(currentPage - 1) * itemsPerPage + 1}–${Math.min(currentPage * itemsPerPage, totalItems)} de ${totalItems} registro(s)`
                : '0 registros'}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1 || loading}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-300 px-2">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages || loading}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminClanRotationLogReport;
